import React, { useCallback, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext'; // Use updated AuthContext
import { useFormContext } from './FormContext'; // Use FormContext for form actions
import { checkUserPermission } from '../utils/firestore'; // Ensure correct import for permission check
import { useLoading } from '../contexts/LoadingContext'; // Use LoadingContext for loading state

// Component handling form creation logic when selecting a template or creating a new form
const CreateFormComponentForTemplate = ({ isNewForm, isCreatingForm, onFormCreated, children }) => {
  const { currentUser } = useAuth(); // Get current user from AuthContext
  const {
    createNewFormInFirestore,
    initializeNewFormLocally,
    setFormId,
    formId
  } = useFormContext(); // Pull all required context values
  const { setLoading } = useLoading(); // Access the setLoading function from LoadingContext
  const isCreatingRef = useRef(false); // Ref to track if form creation is in progress

  // Handler for creating a new form
  const handleCreateNewForm = useCallback(async () => {
    console.log('[CreateFormComponentForTemplate] handleCreateNewForm triggered');

    if (isCreatingRef.current) {
      console.warn('[CreateFormComponentForTemplate] Form creation is already in progress.');
      return;
    }

    try {
      const userId = currentUser?.uid;
      if (!userId) {
        console.warn('[CreateFormComponentForTemplate] User not authenticated');
        alert('User not authenticated');
        return;
      }

      isCreatingRef.current = true;

      setLoading(true, 'formCreation');

      const hasPermission = await checkUserPermission(userId, 'createForms');
      console.log(`[CreateFormComponentForTemplate] User permission to create forms: ${hasPermission}`);

      if (hasPermission) {
        const newFormId = initializeNewFormLocally(userId); // Local only

        if (newFormId) {
          console.log('[CreateFormComponentForTemplate] New form initialized locally with ID:', newFormId);
          setFormId(newFormId); // Set into context for downstream consumption
          localStorage.setItem('formId', newFormId); // Backup in localStorage
          onFormCreated(newFormId); // Navigate to builder
        } else {
          console.error('[CreateFormComponentForTemplate] Failed to create new form ID.');
          alert('Failed to create new form.');
        }
      } else {
        alert('You do not have permission to create new forms.');
      }
    } catch (error) {
      console.error('[CreateFormComponentForTemplate] Error checking permissions or creating new form:', error);
      alert('Failed to verify permissions or create a new form.');
    } finally {
      setLoading(false, 'formCreation');
      isCreatingRef.current = false;
    }
}, [currentUser, initializeNewFormLocally,
    setLoading, setFormId, onFormCreated]);

  // Trigger form creation only when conditions are met
useEffect(() => {
  if (isNewForm && isCreatingForm && currentUser && !isCreatingRef.current) {
    console.log('[CreateFormComponentForTemplate] Starting form creation via effect.');
    handleCreateNewForm();
  }
}, [handleCreateNewForm, currentUser, isNewForm, isCreatingForm]);

  return (
    <div onClick={handleCreateNewForm} style={{ cursor: 'pointer', width: '100%' }}>
      {children}
    </div>
  );
};

// Wrapper that initializes CreateFormComponent only when user has permission
const CreateFormWrapperForTemplate = ({ isNewForm = false, children }) => {
  const [isCreatingForm, setIsCreatingForm] = useState(false);
  const { currentUser } = useAuth();
  const { setLoading } = useLoading();
  const navigate = useNavigate();

  const handleFormCreated = useCallback((newFormId) => {
    console.log('[CreateFormWrapperForTemplate] Form created, navigating to builder with ID:', newFormId);

    navigate(`/form-builder/${newFormId}/design`, {
      state: { createNewForm: true } // ✅ Required for FormContext to skip URL/localStorage
    });

  setIsCreatingForm(false);
}, [navigate, setIsCreatingForm]);          // include it in the dep array

  const handleSelectTemplate = useCallback(async () => {
    console.log('[CreateFormWrapperForTemplate] handleSelectTemplate triggered');

    const userId = currentUser?.uid;
    if (!userId) {
      console.warn('[CreateFormWrapperForTemplate] User not authenticated');
      alert('User not authenticated');
      return;
    }

    if (isNewForm) {
      setLoading(true, 'permissionCheck');

      const hasPermission = await checkUserPermission(userId, 'createForms');
      console.log(`[CreateFormWrapperForTemplate] Permission check result: ${hasPermission}`);

      if (hasPermission) {
        setIsCreatingForm(true); // This causes CreateFormComponentForTemplate to mount
      } else {
        alert('You do not have permission to create new forms.');
      }

      setLoading(false, 'permissionCheck');
    }
  }, [isNewForm, currentUser, setLoading]);

  return (
    <>
      {!isCreatingForm ? (
        <div onClick={handleSelectTemplate} style={{ cursor: 'pointer', width: '100%' }}>
          {children}
        </div>
      ) : (
      <CreateFormComponentForTemplate
        isNewForm={isNewForm}
        isCreatingForm={isCreatingForm}
        onFormCreated={handleFormCreated}
      >
        {children}
      </CreateFormComponentForTemplate>

      )}
    </>
  );
};

export default CreateFormWrapperForTemplate;
